import React, { useEffect, useMemo, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import { InputAdornment, Stack } from "@mui/material";
import { add, sub } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import {
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  mapTaskPriorityToLabel,
  mapTaskStatusToLabel,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { useCreateTransition } from "@fartherfinance/frontend/api/TransitionWorkflows";
import { TransitionType } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";
import { Custodian, ServiceTeamId } from "@fartherfinance/frontend/api/Types";

import {
  assigneeDefaultOption,
  ATTACHMENTS_LIMIT_COUNT,
  custodianOptions,
  MIN_MAX_DUE_DATE_RANGE_YEARS,
  TaskAssigneeAutocompleteOption,
  taskPriorityOptions,
} from "../../Tasks/common";
import {
  useAdvisorsOptions,
  useAssigneesOptions,
  useServiceTeamsOptions,
  useTaskStatusOptionsV2,
} from "../../Tasks/hooks";
import InputHelperText from "../../Tasks/InputHelperText/InputHelperText";
import TaskAttachment from "../../Tasks/TaskDetails/Components/TaskAttachments/TaskAttachment";
import TaskPriorityIcon from "../../Tasks/TaskPriorityIcon/TaskPriorityIcon";
import TaskStatusIcon from "../../Tasks/TaskStatusIcon/TaskStatusIcon";
import { isDateValid } from "../../Tasks/utils";
import TransitionWorkflows from "../../TransitionWorkflows";
import { transitionTypeOptions } from "../common";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useFilesManagement from "@src/multiCustodian/hooks/useFilesManagement";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import {
  EnhancedAutocompleteOption,
  WithGroupMeta,
} from "@src/multiCustodian/pages/Advisor/common";
import {
  getAssigneeForTransitionType,
  getServiceTeamForTransitionType,
} from "@src/multiCustodian/pages/Advisor/components/VirtualSubtasks/constants";
import TransitionVirtualSubtasksSection, {
  createInitialVirtualTasks,
} from "@src/multiCustodian/pages/Advisor/components/VirtualSubtasks/TransitionVirtualSubtasksSection";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import { ComboboxOption } from "@src/sharedComponents/Combobox/Combobox";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import FormBodySpanAllColumns from "@src/sharedComponents/Forms/FormBodySpanAllColumns";
import FormComboboxField from "@src/sharedComponents/Forms/FormComboboxField";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormTextareaField from "@src/sharedComponents/Forms/FormTextareaField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import Text from "@src/sharedComponents/Typography/Text";
import Upload from "@src/sharedComponents/Upload/Upload";

import { mapFormDataToPayload } from "./transitionPayloadMapper";

import styles from "../../Tasks/CreateTask/CreateTask.module.css";

const TRANSITION_TITLE_MAX_LENGTH = 50;
const TRANSITION_TITLE_MAX_LENGTH_ERROR_MSG = `Transition title cannot exceed ${TRANSITION_TITLE_MAX_LENGTH} characters`;

interface FormData {
  title: string;
  description: string;
  transitionType: EnhancedAutocompleteOption<TransitionType>;
  serviceTeam: EnhancedAutocompleteOption<ServiceTeamId> | null;
  advisor: ComboboxOption<string>[];
  assignee: TaskAssigneeAutocompleteOption<string | undefined> | null;
  dueDate: Date | null;
  custodian: EnhancedAutocompleteOption<Custodian> | null;
  taskStatus: EnhancedAutocompleteOption<TaskStatus>;
  taskPriority: EnhancedAutocompleteOption<TaskPriority>;
}

interface CreateTransitionState {
  transitionType: string;
  transitionTypeId: number;
  advisors: ComboboxOption<string>[];
  transitionTemplateId: string;
}

// Tagged union for location state
interface CreateTransitionLocationState {
  type: "createTransition";
  data: CreateTransitionState;
}

type LocationState = CreateTransitionLocationState | undefined;

// Helper functions to create service team and assignee options from configuration
const createServiceTeamOptionFromConfig = (
  serviceTeamConfig: { label: string; value: string } | null
): EnhancedAutocompleteOption<ServiceTeamId> | null => {
  if (!serviceTeamConfig) {
    return null;
  }

  return {
    label: serviceTeamConfig.label,
    value: serviceTeamConfig.value as ServiceTeamId,
  };
};

const createAssigneeOptionFromConfig = (
  assigneeConfig: { label: string; value: string } | null
): TaskAssigneeAutocompleteOption<string | undefined> | null => {
  if (!assigneeConfig) {
    return assigneeDefaultOption;
  }

  return {
    label: assigneeConfig.label,
    value: assigneeConfig.value,
    meta: { type: "PERSON" as const },
  };
};

const CreateTransitionWorkflow: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const notification = useStatusNotification();
  const { enableTaskServiceTeams } = useFlags();

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHelperTextVisible, setIsHelperTextVisible] = useState(false);
  const { files, setFiles, removeFile } = useFilesManagement({
    countLimit: ATTACHMENTS_LIMIT_COUNT,
  });
  const advisorConfig = useAdvisorRequestAuth();
  const createTransition = useCreateTransition(advisorConfig);
  const { advisorsOptions: advisorsApiOptions } = useAdvisorsOptions();
  const { serviceTeamsOptions } = useServiceTeamsOptions();
  const { taskStatusFormOptions } = useTaskStatusOptionsV2();

  const locationState = location.state;
  const createTransitionData =
    locationState?.type === "createTransition" ? locationState : undefined;

  const preselectedAdvisorOptions: ComboboxOption<string>[] =
    createTransitionData?.data.advisors ?? [];
  const initialTransitionType = useMemo(() => {
    if (createTransitionData?.data.transitionType) {
      return (
        transitionTypeOptions.find(
          (option) => option.value === createTransitionData.data.transitionType
        ) || transitionTypeOptions[0]
      );
    }
    return transitionTypeOptions[0];
  }, [createTransitionData]);
  const minDueDate = useMemo(
    () => sub(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS }),
    []
  );
  const maxDueDate = useMemo(
    () => add(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS }),
    []
  );

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      transitionType: initialTransitionType,
      serviceTeam: createServiceTeamOptionFromConfig(
        getServiceTeamForTransitionType(initialTransitionType.value)
      ),
      advisor: preselectedAdvisorOptions,
      assignee: createAssigneeOptionFromConfig(
        getAssigneeForTransitionType(initialTransitionType.value)
      ),
      dueDate: add(new Date(), { days: 7 }),
      custodian: null,
      taskStatus: {
        label: mapTaskStatusToLabel(TaskStatus.enum.NOT_STARTED),
        value: TaskStatus.enum.NOT_STARTED,
      },
      taskPriority: {
        label: mapTaskPriorityToLabel(TaskPriority.enum.MEDIUM),
        value: TaskPriority.enum.MEDIUM,
      },
    },
  });

  const { handleSubmit } = form;
  const [
    formTaskStatus,
    formTaskPriority,
    formServiceTeam,
    formAssignee,
    formAdvisor,
    formTransitionType,
  ] = form.watch([
    "taskStatus",
    "taskPriority",
    "serviceTeam",
    "assignee",
    "advisor",
    "transitionType",
  ]);

  const isServiceTeamFieldVisibleForCX = enableTaskServiceTeams;
  const isMissingServiceTeamAlertVisible = false;
  const isAttachmentsSectionVisible = files.length > 0;
  const [virtualTasks, setVirtualTasks] = useState(
    createInitialVirtualTasks(formTransitionType.value)
  );

  useEffect(() => {
    setVirtualTasks(createInitialVirtualTasks(formTransitionType.value));

    const newServiceTeam = createServiceTeamOptionFromConfig(
      getServiceTeamForTransitionType(formTransitionType.value)
    );
    const newAssignee = createAssigneeOptionFromConfig(
      getAssigneeForTransitionType(formTransitionType.value)
    );

    form.setValue("serviceTeam", newServiceTeam);
    form.setValue("assignee", newAssignee);
  }, [formTransitionType, form]);

  const handleCloseDrawer = (): void => {
    setIsDrawerOpen(false);
  };

  const handleFormSubmit = async (formData: FormData): Promise<void> => {
    try {
      setIsSubmitting(true);

      const payload = mapFormDataToPayload(formData, virtualTasks);
      await createTransition({ request: payload });

      notification("Transition created successfully.", "Success");
      setIsDrawerOpen(false);
    } catch (error) {
      let errorMessage = "Error creating transition. Please try again.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      notification(errorMessage, "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePersistFiles = (files: FileList | null): void => {
    if (files && files.length) {
      setFiles(files);
    }
  };

  const handleCreateMenuOptions = (fileName: string): MenuOption[] => {
    return [
      {
        label: "Delete",
        className: styles.textWarning,
        onClick: () => removeFile(fileName),
      },
    ];
  };

  const handleClearAutocompleteField = (field: keyof FormData): void => {
    form.resetField(field, { defaultValue: null });
  };

  const assigneesQueryParams = useMemo(() => {
    const selectedAdvisors = formAdvisor as ComboboxOption<string>[];
    const firstAdvisorId =
      selectedAdvisors && selectedAdvisors[0]
        ? (selectedAdvisors[0].value as any)
        : undefined;
    return {
      serviceTeam: formServiceTeam?.value,
      advisor: firstAdvisorId as any,
    };
  }, [formServiceTeam, formAdvisor]);

  const { assigneesOptions, assigneesQuery } = useAssigneesOptions(
    assigneesQueryParams,
    true
  );

  const extendedAssigneesOptions = useMemo(
    () => [assigneeDefaultOption, ...assigneesOptions],
    [assigneesOptions]
  );

  // Watch for field changes and reset dependent fields
  useEffect(() => {
    const watchSubscription = form.watch((values, { name, type }) => {
      if (type === "change" && name === "description") {
        setIsHelperTextVisible((values.description ?? "").length > 2);
      }

      // Reset assignee when advisor changes
      if (
        (name === "serviceTeam" || name === "advisor") &&
        values.assignee?.value
      ) {
        form.resetField("assignee", { defaultValue: null });
      }
    });

    return () => watchSubscription.unsubscribe();
  }, [form]);

  // Handle navigation when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      history.push("/Advisor/Transitions");
    }
  }, [isDrawerOpen, history]);

  if (!createTransitionData) {
    return (
      <TransitionWorkflows>
        <Drawer isDrawerOpen={isDrawerOpen} onClose={handleCloseDrawer}>
          <div style={{ padding: "20px" }}>
            <Text variant="title2">Error</Text>
            <Text>No transition data provided</Text>
          </div>
        </Drawer>
      </TransitionWorkflows>
    );
  }

  return (
    <TransitionWorkflows>
      <FormProvider {...form}>
        <Drawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          footer={
            <Stack
              sx={{ width: "100%" }}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Upload
                multiple
                onChange={handlePersistFiles}
                renderChild={(onClick) => (
                  <IconButton
                    iconClassName={styles.attachmentIcon}
                    onClick={onClick}
                    IconComponent={AttachFileIcon}
                  />
                )}
              />

              <div className={styles.actions}>
                <Button
                  variant="outlined"
                  buttonType="primary"
                  text="Cancel"
                  onClick={handleCloseDrawer}
                  disabled={isSubmitting}
                />
                <Button
                  variant="contained"
                  buttonType="primary"
                  text={isSubmitting ? "Creating..." : "Save"}
                  disabled={isSubmitting}
                  onClick={handleSubmit(handleFormSubmit)}
                />
              </div>
            </Stack>
          }
        >
          {isMissingServiceTeamAlertVisible && (
            <AlertInline
              className={styles.alert}
              severity="error"
              message="Cannot create transition because advisor not assigned to service team. Contact Client Services to resolve."
            />
          )}

          <div className={styles.header}>
            <Text variant="title2">Create Transition</Text>
          </div>

          <FormBody>
            <FormBodySpanAllColumns>
              <FormTextField
                name="title"
                label="Title (50 character limit)"
                required="Must not be empty"
                rules={{
                  validate: {
                    max50: (value) =>
                      value.length > TRANSITION_TITLE_MAX_LENGTH
                        ? TRANSITION_TITLE_MAX_LENGTH_ERROR_MSG
                        : true,
                  },
                }}
              />
            </FormBodySpanAllColumns>

            <FormBodySpanAllColumns>
              <div className={styles.description}>
                <FormTextareaField
                  name="description"
                  label="Description (Optional)"
                  placeholder="Describe the transition and provide additional details..."
                  minRows={5}
                />
              </div>
              <Spacer verticalSpacing="4px" />
              <InputHelperText isVisible={isHelperTextVisible} />
            </FormBodySpanAllColumns>

            <FormDropdownField<EnhancedAutocompleteOption<string>>
              name="transitionType"
              label="Type"
              values={transitionTypeOptions}
              required="Please select a transition type"
              getOptionKey={(option) => option.value}
              onClear={() => handleClearAutocompleteField("transitionType")}
            />

            <FormComboboxField
              name="advisor"
              label="Advisor"
              values={advisorsApiOptions}
              rules={{
                validate: {
                  notEmpty: (val: ComboboxOption<string>[]) =>
                    val && val.length > 0 ? true : "Please select an advisor",
                },
              }}
            />

            {isServiceTeamFieldVisibleForCX && (
              <FormDropdownField<EnhancedAutocompleteOption<ServiceTeamId>>
                name="serviceTeam"
                label={
                  formAssignee && formAssignee.value
                    ? "Service Team (Optional)"
                    : "Service Team"
                }
                values={serviceTeamsOptions}
                required={
                  formAssignee && formAssignee.value
                    ? false
                    : "Please select a service team"
                }
                getOptionKey={(option) => option.value}
                onClear={() => handleClearAutocompleteField("serviceTeam")}
              />
            )}

            <FormDropdownField<
              TaskAssigneeAutocompleteOption<string | undefined>
            >
              name="assignee"
              label={formServiceTeam ? "Assignee (Optional)" : "Assignee"}
              values={extendedAssigneesOptions}
              required={formServiceTeam ? false : "Please select an assignee"}
              isLoading={assigneesQuery.isLoading}
              getOptionKey={(option) => option.value ?? "undefined"}
              onClear={() => handleClearAutocompleteField("assignee")}
            />

            <FormDateField
              name="dueDate"
              label="Due Date"
              placeholder=""
              helperText="MM/DD/YYYY"
              minDate={minDueDate}
              maxDate={maxDueDate}
              required="Please select a due date"
              rules={{
                validate: { isDateValid },
              }}
            />

            <FormDropdownField<EnhancedAutocompleteOption<Custodian>>
              name="custodian"
              label="Custodian"
              values={custodianOptions}
              required="Please select a custodian"
              getOptionKey={(option) => option.value}
              onClear={() => handleClearAutocompleteField("custodian")}
            />

            <FormDropdownField<
              EnhancedAutocompleteOption<TaskStatus> & WithGroupMeta
            >
              name="taskStatus"
              label="Status"
              values={taskStatusFormOptions}
              getOptionKey={(option) => option.value}
              groupBy={(option) => option.meta.group}
              renderOption={(optionProps, option) => (
                <li
                  {...optionProps}
                  className={toClassName(optionProps.className, styles.option)}
                >
                  <TaskStatusIcon taskStatus={option.value} />
                  <Text variant="body">{option.label}</Text>
                </li>
              )}
              renderStartAdornment={() => (
                <InputAdornment position="start">
                  <TaskStatusIcon taskStatus={formTaskStatus.value} />
                </InputAdornment>
              )}
            />

            <FormDropdownField<EnhancedAutocompleteOption<TaskPriority>>
              name="taskPriority"
              label="Priority"
              values={taskPriorityOptions}
              getOptionKey={(option) => option.value}
              renderOption={(optionProps, option) => (
                <li
                  {...optionProps}
                  className={toClassName(optionProps.className, styles.option)}
                >
                  <TaskPriorityIcon value={option.value} />
                  <Text variant="body">{option.label}</Text>
                </li>
              )}
              renderStartAdornment={() => (
                <InputAdornment position="start">
                  <TaskPriorityIcon value={formTaskPriority.value} />
                </InputAdornment>
              )}
            />
          </FormBody>

          {isAttachmentsSectionVisible && (
            <div className={styles.attachmentsContainer}>
              <div className={styles.attachmentsHeader}>
                <Text variant="title3">Attachments</Text>
                <Text variant="body" color="textSubtle">
                  {files.length}/{ATTACHMENTS_LIMIT_COUNT}
                </Text>
              </div>

              <div className={styles.attachments}>
                {files.map((file) => (
                  <TaskAttachment
                    key={file.name}
                    previewUrl={URL.createObjectURL(file)}
                    filename={file.name}
                    menuOptions={handleCreateMenuOptions(file.name)}
                  />
                ))}
              </div>
            </div>
          )}
          <Spacer verticalSpacing="24px" />

          {/* In-memory subtasks section specific to transition creation */}
          <TransitionVirtualSubtasksSection
            initialTasks={virtualTasks}
            onChange={setVirtualTasks}
            transitionType={formTransitionType.value}
          />

          {isSubmitting && (
            <div className={styles.loading}>
              <LogoLoadingStill />
            </div>
          )}
        </Drawer>
      </FormProvider>
    </TransitionWorkflows>
  );
};

export default CreateTransitionWorkflow;
